/**
 * Floating support chat widget — bottom-right, 360x480 popover.
 *
 * Owns the per-conversation state:
 *  - chat_id (uuid generated on first open)
 *  - messages array (kept in React state; no intermediate DB writes)
 *  - sessionId (when opened from EarningHistoryPage's per-row button)
 *  - pointsGrantedTotal (mirrored from server responses to write at /close)
 *  - 10-min inactivity timer → auto-close
 *  - requires_logout flag → 3s delay, signOut(), navigate('/')
 *
 * Pairing: the small floating FAB lives in NavShell; this widget renders
 * its popover when useSupportChat().isOpen is true. The widget itself
 * doesn't render the FAB (NavShell already has one wired to the existing
 * help-chat — see NavShell.tsx).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, X, AlertCircle, Calendar, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { API_BASE } from '../lib/apiBase';
import { useAuth } from '../hooks/useAuth';
import { useSupportChat } from '../hooks/useSupportChat';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatResponse {
  message: string;
  action: { type: string; [k: string]: unknown } | null;
  requires_logout?: boolean;
  calendly_url?: string;
  points_granted?: number;
}

const INACTIVITY_MS = 10 * 60 * 1000;
const LOGOUT_DELAY_MS = 3_000;

function newChatId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for ancient browsers — not crypto-secure, but uniqueness is
  // all we need (server uses it as a row PK).
  return `chat_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export default function SupportChatWidget() {
  const { isOpen, sessionId, close } = useSupportChat();
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const [chatId, setChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [calendlyUrl, setCalendlyUrl] = useState<string | null>(null);
  const [pointsGrantedTotal, setPointsGrantedTotal] = useState(0);
  const [resolution, setResolution] = useState<'resolved' | 'escalated_email' | 'escalated_calendly' | 'abandoned'>('abandoned');

  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // ── Reset state on each new open ─────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    setChatId(newChatId());
    setMessages([
      {
        role: 'assistant',
        content: sessionId
          ? "Hi! I see you have a question about a recent survey. What's going on?"
          : "Hi! I'm here to help — what can I do for you?",
      },
    ]);
    setInput('');
    setError(null);
    setCalendlyUrl(null);
    setPointsGrantedTotal(0);
    setResolution('abandoned');
  }, [isOpen, sessionId]);

  // ── Auto-scroll on new messages ──────────────────────────────────────────
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // ── Close: write support_chat row on the way out ─────────────────────────
  const writeCloseRow = useCallback(async (
    finalMessages: Message[],
    finalChatId: string,
    finalResolution: typeof resolution,
    finalPoints: number,
  ): Promise<void> => {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) return;
      await fetch(`${API_BASE}/api/member/support/chat/close`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: finalChatId,
          ...(sessionId ? { session_id: sessionId } : {}),
          messages: finalMessages,
          resolution: finalResolution,
          points_granted: finalPoints,
        }),
      });
    } catch {
      // Silent — the close call is best-effort. If it fails the chat is
      // simply uncounted in admin observability; abuse-limit math is
      // skewed by one but the member's experience is unaffected.
    }
  }, [sessionId]);

  // ── Inactivity timer ─────────────────────────────────────────────────────
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      // Auto-close after 10 min of no activity. Fires writeCloseRow with
      // the current state. Capturing in closure is fine because the timer
      // resets on every send.
      if (chatId && messages.length > 1) {
        void writeCloseRow(messages, chatId, resolution, pointsGrantedTotal);
      }
      close();
    }, INACTIVITY_MS);
  }, [chatId, messages, resolution, pointsGrantedTotal, writeCloseRow, close]);

  useEffect(() => {
    if (!isOpen) {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      return;
    }
    resetInactivityTimer();
    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [isOpen, resetInactivityTimer]);

  // ── Cleanup on unmount: flush close (e.g. user navigates away) ───────────
  useEffect(() => {
    return () => {
      if (chatId && messages.length > 1 && isOpen) {
        void writeCloseRow(messages, chatId, resolution, pointsGrantedTotal);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Send a turn ──────────────────────────────────────────────────────────
  async function handleSend(): Promise<void> {
    const trimmed = input.trim();
    if (!trimmed || sending || !chatId) return;

    const next: Message[] = [...messages, { role: 'user', content: trimmed }];
    setMessages(next);
    setInput('');
    setSending(true);
    setError(null);
    resetInactivityTimer();

    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(`${API_BASE}/api/member/support/chat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: chatId,
          ...(sessionId ? { session_id: sessionId } : {}),
          messages: next,
        }),
      });

      if (!res.ok) {
        // Surface the status code + server-supplied error text. The bare
        // "Request failed" fallback was masking actionable failures (most
        // commonly 500 when the support_chat table isn't migrated yet,
        // or 404 when the API deploy hasn't caught up to the frontend
        // build). Try JSON first; fall back to a text excerpt if the
        // server returned a stack trace or HTML error page.
        const text = await res.text().catch(() => '');
        let serverError: string | undefined;
        try {
          const parsed = JSON.parse(text) as { error?: string };
          serverError = parsed.error;
        } catch {
          serverError = text.slice(0, 200) || undefined;
        }
        throw new Error(`${res.status} ${serverError ?? 'Request failed'}`);
      }

      const body = (await res.json()) as ChatResponse;
      const updated: Message[] = [...next, { role: 'assistant', content: body.message }];
      setMessages(updated);

      if (typeof body.points_granted === 'number' && body.points_granted > 0) {
        setPointsGrantedTotal((p) => p + (body.points_granted ?? 0));
      }
      if (body.calendly_url) {
        setCalendlyUrl(body.calendly_url);
      }
      if (body.action?.type === 'escalate_email') {
        setResolution('escalated_email');
      } else if (body.action?.type === 'escalate_calendly') {
        setResolution('escalated_calendly');
      } else if (
        body.action?.type === 'grant_points' ||
        body.action?.type === 'send_password_reset' ||
        body.action?.type === 'update_phone' ||
        body.action?.type === 'update_preferences'
      ) {
        setResolution('resolved');
      }

      if (body.requires_logout) {
        // 1. Show the AI's final message (already in `updated` above).
        // 2. Write the close row with resolution='resolved'.
        // 3. 3s delay → signOut → navigate('/').
        await writeCloseRow(updated, chatId, 'resolved', pointsGrantedTotal);
        setTimeout(() => {
          void (async () => {
            try {
              await signOut();
            } finally {
              navigate('/');
            }
          })();
        }, LOGOUT_DELAY_MS);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      setError(msg);
    } finally {
      setSending(false);
    }
  }

  function handleManualClose(): void {
    if (chatId && messages.length > 1) {
      void writeCloseRow(messages, chatId, resolution, pointsGrantedTotal);
    }
    close();
  }

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-44 sm:bottom-24 right-4 sm:right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[480px] max-h-[calc(100vh-8rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-primary text-white">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-semibold">Support</span>
        </div>
        <button
          type="button"
          onClick={handleManualClose}
          className="text-white/80 hover:text-white"
          aria-label="Close chat"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#F8F9FA]">
        {messages.map((m, i) => (
          <div
            key={i}
            className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
              m.role === 'user'
                ? 'bg-primary text-white ml-auto rounded-br-md'
                : 'bg-white text-[#111827] border border-gray-100 rounded-bl-md'
            }`}
          >
            {m.content}
          </div>
        ))}
        {sending && (
          <div className="flex items-center gap-2 text-xs text-[#6B7280]">
            <Loader2 className="w-3 h-3 animate-spin" />
            Thinking…
          </div>
        )}
        {calendlyUrl && (
          <a
            href={calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-full hover:bg-amber-600 transition-colors"
          >
            <Calendar className="w-4 h-4" />
            Schedule a call
          </a>
        )}
        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSend();
        }}
        className="border-t border-gray-100 p-2 bg-white flex items-center gap-2"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            resetInactivityTimer();
          }}
          placeholder="Type your message…"
          disabled={sending}
          className="flex-1 px-3 py-2 text-sm bg-[#F8F9FA] border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-primary text-white hover:bg-primary-dark disabled:opacity-40"
          aria-label="Send"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
